// American University: public college page (#2652, batch 2), official facts
// only.
//
// Every figure below was read on 2026-10-01 from the source it names:
// #3859 (2026-10-11), page vs KB: the cost table now follows the school's
// own 2026-27 cost of attendance page (cited as its own source), the figures
// the KB holds; the Common Data Set G1 budget it showed before differs.
//  - AU's Common Data Set 2025-2026 (CDS-PDF-2025-2026_PDF_American-
//    University_WEB_FINAL_04-23-26.pdf from american.edu/provost/oira): C1
//    applicants/admits/enrollees for Fall 2025 with the residency table
//    (which also has an "unknown" column: 28 applied, 25 admitted); C8 test
//    policy for Fall 2027; C21/C22 plan dates; G1/G5 2026-2027 costs
//    (first-year column). Quirk: C21 labels its early decision counts
//    (392 applied, 316 admitted) "For the Fall 2024 entering class", so they
//    are left out rather than shown beside the fall 2025 totals.
//  - american.edu admissions pages (Decision Plans and Deadlines, the
//    First-Year Application Checklist with the 2026-2027 essay prompt and the
//    test-optional policy), read in a headless browser because the site sits
//    behind a bot check. The deadlines page carries no year; it is the live
//    page for this cycle, read during the 2026-27 application season.
//  - College Scorecard, school id 131159 (net price by family income). The
//    API's DEMO_KEY was over its rate limit, so the figures were read from
//    the Scorecard's own school page, which shows the same latest fields.
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'AU Common Data Set 2025-2026',
        url: 'https://www.american.edu/provost/oira/upload/CDS-PDF-2025-2026_PDF_American-University_WEB_FINAL_04-23-26.pdf',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'AU Admissions, Decision Plans and Deadlines',
        url: 'https://www.american.edu/admissions/first-year/decision-plans-and-deadlines.cfm',
        read: '2026-10-01',
    },
    checklist: {
        label: 'AU Admissions, First-Year Application Checklist',
        url: 'https://www.american.edu/admissions/first-year/checklist.cfm',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?131159-American-University',
        read: '2026-10-15',
    },
    coa: {
        label: 'American University Financial Aid, Undergraduate First-Year Students 2026-2027 Cost of Attendance',
        url: 'https://www.american.edu/financialaid/cost-of-attendance/undergraduate-first-year-students.cfm',
        read: '2026-10-11',
    },
};

const college = {
    slug: 'american-university',
    kbId: 'american_university',
    name: 'American University',
    shortName: 'American',
    aliases: ['AU', 'American U'],
    location: 'Washington, DC',
    control: 'Private',
    lastChecked: '2026-10-11',
    sources: SOURCES,
    // From the KB's sourced branding (AU style guide); decoration only.
    branding: {
        primary: '#004FA2',
        secondary: '#E0263C',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/american_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 20960,
        admitted: 13741,
        enrolled: 1545,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 441, admitted: 197 },
            { group: 'Out-of-state', applied: 19194, admitted: 12864 },
            { group: 'International', applied: 1297, admitted: 655 },
            { group: 'Unknown', applied: 28, admitted: 25 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'AU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'The Common Data Set labels its early decision counts as fall 2024 figures and gives no early action counts, so it gives no early acceptance rate for fall 2025.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to AU as a whole. A few programs ask for more:',
        rows: [
            { school: 'Dance, Music, Musical Theatre, Popular Music Performance & Enterprise, Theatre', requirement: 'Auditions, online or in person, through the Department of Performing Arts SlideRoom portal. AU strongly encourages completing them before you enroll.' },
            { school: 'Audio Technology, Audio Production', requirement: 'An optional portfolio through SlideRoom, strongly encouraged.' },
            { school: 'Film and Media Arts, Photography (School of Communication)', requirement: 'An optional creative portfolio through the School of Communication\'s SlideRoom.' },
        ],
        source: 'checklist',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'Mid-January', binding: false },
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Mid-December', binding: true },
            { plan: 'Early Decision II', deadline: 'January 15', decision: 'Mid-February', binding: true },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'April 1', binding: false },
        ],
        note: 'Early Action applicants hear by January 31 at the latest; deferred applications get a final decision by April 1. If you are denied in one round, you cannot reapply in another round the same year.',
        source: 'deadlines',
        binding: 'Both Early Decision rounds are binding: you and your family agree, when you apply, to attend AU if admitted. You, a parent or guardian and your counselor sign an Early Decision Agreement. Once admitted, you withdraw your other applications and send no new ones. You can still apply early action elsewhere, but you withdraw those applications if AU admits you.',
        bindingSource: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'AU is test-optional. If you want your SAT or ACT considered, you can self-report scores on the application; if admitted and you enroll, you send official scores. Students graduating from high school outside the U.S. are not required to send the SAT or ACT.',
        source: 'checklist',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 62680 },
            { item: 'Mandatory fees', amount: 1700 },
            { item: 'Housing (on campus, double room)', amount: 13112 },
            { item: 'Food (meal plan)', amount: 6584 },
            { item: 'Metro pass fee', amount: 272 },
            { item: 'Books and supplies', amount: 1200 },
            { item: 'Transportation', amount: 692 },
            { item: 'Personal expenses', amount: 1080 },
            { item: 'Loan fees', amount: 54 },
        ],
        total: 87374,
        note: 'First-year students living on campus.',
        source: 'coa',
        netPrice: {
            average: 41302,
            byIncome: [
                { income: '$0 to $30,000', amount: 28397 },
                { income: '$30,001 to $48,000', amount: 19487 },
                { income: '$48,001 to $75,000', amount: 27150 },
                { income: '$75,001 to $110,000', amount: 31984 },
                { income: 'Over $110,000', amount: 53571 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from AU\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
